/*
 * ENTITY TYPEAHEAD COMPONENT
 * 
 * Provides a search dropdown interface for accounts and contacts with visual distinction
 * between existing and suggested contacts.
 * 
 * Key Features:
 * - Three-section layout: Accounts, Existing Contacts, Suggested Contacts
 * - Visual distinction using section organization:
 *   * Accounts: Blue avatar
 *   * Existing Contacts: Red avatar, in "Existing Contacts" section
 *   * Suggested Contacts: Red avatar, in "Suggested Contacts" section
 * - Fake ID detection: Contacts with ID >= 99000000 are classified as suggested
 * - Real-time search via SearchAll API endpoint
 * - Keyboard navigation and accessibility support
 * 
 * Updated Logic:
 * - Uses `isSuggested` property instead of `hasId` for better fake ID handling
 * - Properly distinguishes between real contact IDs (< 99000000) and fake IDs (>= 99000000)
 * - Ensures suggested contacts from RLP database are visually distinct from CRM contacts
 * - Forwards explicit accessible labels and input styling without coupling them to placeholder copy
 */

import React from 'react';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import Typeahead, { TypeaheadResult, Section } from './Typeahead';

export interface EntityTypeaheadResult extends TypeaheadResult {
  email?: string;
}

interface EntityTypeaheadProps {
  onSelect: (result: TypeaheadResult) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  inputClassName?: string;
}

const EntityTypeahead: React.FC<EntityTypeaheadProps> = ({
  onSelect,
  placeholder = 'Search contacts and accounts...',
  ariaLabel,
  className = '',
  inputClassName = ''
}) => {
  const sections: Section[] = [
    {
      title: 'Accounts',
      filter: { type: 'Account' },
      avatarColor: 'bg-blue-500'
    },
    {
      title: 'Existing Contacts',
      filter: { type: 'Contact', isSuggested: false },
      avatarColor: 'bg-red-500'
    },
    {
      title: 'Suggested Contacts',
      filter: { type: 'Contact', isSuggested: true },
      avatarColor: 'bg-red-500'
    }
  ];

  return (
    <Typeahead
      onSelect={onSelect}
      sections={sections}
      searchEndpoint={API_ENDPOINTS.GET_CONTACTS_SEARCH_ALL}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      className={className}
      inputClassName={inputClassName}
    />
  );
};

export default EntityTypeahead;
